import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { CheckBox, YesNoField } from "../../assets";
import { getShopOrders, updateShop } from "../../actions/shop";
import OrderTile from "./OrderTile";
import { useInterval } from "../../assets/hooks";
import { requestNotificationPermission } from "../../actions/session";
import { useModal } from "../../assets/useModal";
import moment from "moment";
import { PAID, REJECTED, COMPLETED, COLORS, DELIVERY } from "../../constants";
import { useState } from "react";
import { useNotification } from "../../assets/hooks/useNotification";
import { useCallback } from "react";
import { groupArrayByField } from "../../helpers";

const newOrdersQuery = {
	status: PAID,
	_sort: "created_at:asc"
}

const currentOrdersQuery = {
	status_nin: [COMPLETED, REJECTED, PAID],
	_sort: "eta:asc"
}

function filtersToQuery(filters) {
	return {
		created_at_gte: filters.from.format(),
		created_at_lte: filters.to.format(),
		status_in: filters.statuses
	}
}

export default function ShopOrders() {

	const { openModal, setModal } = useModal();
	const [filters, setFilters] = useState({
		from: moment().subtract(1, "week"),
		to: moment(),
		statuses: [COMPLETED]
	});
	const dispatch = useDispatch();
	const {
		shop,
		shop: { orders },
		session: { notificationState }
	} = useSelector(state => ({
		shop: state.shop,
		session: { notificationState: state.session.notificationState }
	}));

	const { notify } = useNotification();
	const onNewOrders = useCallback(newOrders => {
		if (shop.notificationSounds && newOrders.filter(order => order.status === "paid").length) notify();
	}, [notify, shop.notificationSounds]);

	useEffect(() => {
		dispatch(getShopOrders(currentOrdersQuery));
		dispatch(getShopOrders(newOrdersQuery, onNewOrders));
		dispatch(requestNotificationPermission());
	}, [dispatch, onNewOrders]);

	useInterval(() => {
		dispatch(getShopOrders(newOrdersQuery, onNewOrders));
	}, 20000);

	useInterval(() => {
		dispatch(getShopOrders(currentOrdersQuery));
	}, 65000);

	function openOrder(order) {
		setModal(<OrderViewer orderId={order.id} />, `order_${order.id}_main`);
		openModal();
	}

	return <div className="shopOrders">
		<section>
			<h4>Your shop orders</h4>
			<div className="inner">
				This is the list of your shop orders. Here you can review them and change their statuses.
            </div>
		</section>
		<section>
			<h4>Play sound on new order?</h4>
			<div className="inner">
				Select yes if you want to play a notification sound whenever there are unconfirmed orders in the list. Useful if you don't want to miss anything!
				<br /><br />
			</div>
			<div className="inner">
				<YesNoField
					value={shop.notificationSounds}
					onChange={notificationSounds => dispatch(updateShop({ notificationSounds }))}
					size={40}
					fontSize={"1.6rem"}
				/>
			</div>
		</section>
		{notificationState === "denied" && <section>
			<h4>Keeping updated</h4>
			<div className="inner">
				You have disabled push notifications. These are useful because they alert you when new orders come in. To re-enable them have a look at <a href="https://sendpulse.com/knowledge-base/push-notifications/enable-disable-push-notifications-google-chrome" target="_blank" rel="noopener noreferrer">this guide</a>.
			</div>
		</section>}
		<br /><br />
		<section>
			<h4>Unconfirmed Orders</h4>
			<div className="inner">
				These are the latest orders received that need either confirming or rejecting.
				<br /><br />
			</div>
			<div className="inner">
				<OrderTable
					orders={orders.filter(order => [PAID].includes(order.status))}
					onSelect={openOrder}
				/>
			</div>
		</section>
		<br /><br />
		<section>
			<h4>Active Orders</h4>
			<div className="inner">
				These are your current active orders. Your goal is to get them all to 'completed'.
				<br /><br />
			</div>
			<div className="inner">
				<OrderTable
					orders={orders.filter(order => ![COMPLETED, REJECTED, PAID].includes(order.status))}
					onSelect={openOrder}
				/>
			</div>
		</section>
		<br /><br />
		<section>
			<h4>Past Orders</h4>
			<div className="inner">
				Search for completed or rejected orders. Select to/from dates and the statuses you want to find, then press the search button.
				<br /><br />
			</div>
			<div className="inner flexy featureFont" style={{ fontSize: "1.3rem" }}>
				<b style={{ marginRight: 5, marginBottom: 10 }}>From</b>
				<b style={{ marginLeft: 5, marginBottom: 10 }}>To</b>
			</div>
			<div className="inner flexy">
				<input style={{ marginRight: 5 }}
					type="date"
					value={filters.from.format("YYYY-MM-DD")}
					onChange={e => setFilters({ ...filters, from: moment(e.target.value) })}
				/>
				<input style={{ marginLeft: 5 }}
					type="date"
					value={filters.to.format("YYYY-MM-DD")}
					onChange={e => setFilters({ ...filters, to: moment(e.target.value) })}
				/>
			</div>
			<div className="inner flexy wrapping featureFont" style={{ marginBottom: 10 }}>
				{/* Select / deselect completed status */}
				<div
					className="v-middle"
					style={{ padding: "10px 0px", minWidth: 150, cursor: "pointer" }}
					onClick={e => setFilters({
						...filters,
						statuses: filters.statuses.includes(COMPLETED) ?
							filters.statuses.filter(s => s !== COMPLETED) : [...filters.statuses, COMPLETED]
					})}
				>
					<CheckBox
						color={`rgb(${COLORS[COMPLETED].join(",")})`}
						checked={filters.statuses.includes(COMPLETED)}
					/>
					<b style={{ fontSize: "1.3rem", marginLeft: 10 }}>{COMPLETED}</b>
				</div>
				{/* Select / deselect completed status */}
				<div
					className="v-middle"
					style={{ padding: "10px 0px", minWidth: 150, cursor: "pointer" }}
					onClick={e => setFilters({
						...filters,
						statuses: filters.statuses.includes(REJECTED) ?
							filters.statuses.filter(s => s !== REJECTED) : [...filters.statuses, REJECTED]
					})}
				>
					<CheckBox
						color={`rgb(${COLORS[REJECTED].join(",")})`}
						checked={filters.statuses.includes(REJECTED)}
					/>
					<b style={{ fontSize: "1.3rem", marginLeft: 10 }}>{REJECTED}</b>
				</div>
				{/* Start the search */}
				<button
					className="brand-bg"
					onClick={e => dispatch(getShopOrders(filtersToQuery(filters)))}
					style={{
						minWidth: 200
					}}
				>Search</button>
			</div>
			<div className="inner">
				<hr />
			</div>
			<div className="inner">
				<OrderTable
					orders={orders
						.filter(order => (
							filters.statuses.includes(order.status) &&
							moment(order.created_at).isSameOrAfter(filters.from) &&
							moment(order.created_at).isSameOrBefore(filters.to)
						))
						.sort((a, b) => moment(b.created_at).valueOf() - moment(a.created_at).valueOf())
					}
					onSelect={openOrder}
				/>
			</div>
		</section>
	</div>
}

function OrderViewer({ orderId }) {
	const order = useSelector(state => state.shop.orders.find(order => order.id === orderId));
	return <OrderTile
		editable={true}
		key={order.id}
		order={order}
	/>
}

function OrderTable({ orders, onSelect }) {

	if (!orders.length) return <div className="order-table-list-empty featureFont text-center" style={{
		padding: 10,
		backgroundColor: "#f7f7f7",
		fontSize: "1.3rem"
	}}>
		<b>Nothing in this list..</b>
	</div>

	return <table className="order-table">
		<thead className="featureFont">
			<tr>
				<th>#ID</th>
				<th>Type</th>
				<th>Times</th>
				<th>Summary</th>
				<th>Customer</th>
				<th className="status">Status</th>
			</tr>
		</thead>
		<tbody>
			{orders.map(order => {

				let formattedAddress;
				if (order.deliveryAddress) formattedAddress = <>
					<span>{order.deliveryAddress.line1}</span><br />
					<span>{order.deliveryAddress.line2}</span><br />
					<span>{order.deliveryAddress.postCode}</span>
				</>

				const orderTotal = order.items
					.reduce((acc, item) => (item.price * item.quantity) + acc, 0);

				// Render a table row for each order.
				return <tr
					key={order.id}
					onClick={e => onSelect(order)}
					className={[
						"order-row",
						`order-row-${order.status}`,
						`status-${order.status}-border-color`
					].join(" ")}

				>

					<td className="id featureFont brand-c">#{order.id}</td>
					<td className="type featureFont">{order.type.slice(0, 3)}</td>
					{/* Important times for the order */}
					<td>
						<div>Made: {moment(order.created_at).format("Do MMM HH:mm")}</div>
						{Boolean(order.chosenTime) && <div>Chosen: {moment(order.chosenTime).format("Do MMM HH:mm")}</div>}
						{Boolean(order.eta) && <div>ETA: {moment(order.eta).format("Do MMM HH:mm")}</div>}
					</td>
					{/* Order summary */}
					<td>
						{order.items
							.filter(({ name }) => !["Service charge", "Delivery"].includes(name))
							.map(({ name, quantity, id, options }) => <div className="line-item-preview" key={id}>
								<div>{quantity} x {name}</div>
								{Object.entries(groupArrayByField(options, "name")).map(([name, options]) => (
									<div className="line-item-addOn-type" key={name}>
										<b>{name}: </b>
										{/* And then iterate each of the options */}
										<span>
											{options.map(({ addOn, quantity: addOnQuantity }) => (
												`${addOn}${addOnQuantity !== 1 ? ` x${addOnQuantity}` : ""}`
											)).join(" | ")}
										</span>
									</div>
								))}
							</div>)}
						<div className="featureFont" style={{
							fontSize: "1.1rem",
							marginTop: 5
						}}><b>£{(orderTotal / 100).toFixed(2)}</b></div>
					</td>
					{/* User details */}
					<td>
						{Boolean(order.user.firstName) && <div className="name">
							{order.user.firstName} {order.user.lastName}
						</div>}
						{Boolean(order.user.email) && <div
							className="email"
							onClick={e => e.stopPropagation()}
						>
							<a href={`mailto:${order.user.email}`}>
								{order.user.email}
							</a>
							<br /><br />
						</div>}
						{Boolean(order.user.tel) && <div
							className="tel"
							onClick={e => e.stopPropagation()}
						>
							<a href={`tel:${order.user.tel}`}>{order.user.tel}</a>
							<br /><br />
						</div>}
						{Boolean(formattedAddress && order.type === DELIVERY) && <div>
							{formattedAddress}
							{order.deliveryAddress.mapLink && <>
								<br />
								<a
									style={{ display: "block" }}
									target="_blank"
									rel="noopener noreferrer"
									href={order.deliveryAddress.mapLink}
									onClick={e => e.stopPropagation()}
								>View on map >></a>
							</>}
						</div>}
					</td>
					{/* Order status */}
					<td className={`status featureFont status-${order.status}-color`}>
						<div className={`overlay status-${order.status}-bg-color`} />
						<div>{order.status}</div>
					</td>
				</tr>
			})}
		</tbody>
	</table>
}